import { useState } from 'react';
import { userAppContext } from '../../context/Context';
import PricePlanesAcademic from '../Pricings/PricePlanesAcademic';
import AdvancedPlanOnly from '../Pricings/AdvancedPlanOnly';
import Swal from 'sweetalert2';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import AcademicApplicationModal from '../Academic/AcademicApplicationModal';
import RequestPricingService from '../../services/RequestPricingService';
import SharedButton from '@/global/Button/Button';
import { XMarkIcon } from '@heroicons/react/24/solid';

const UpgradePlan = () => {
  const { user, groups = [] } = userAppContext();
  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const isStandard = groups?.includes('ROLE_STANDARD');

  const [showModal, setShowModal] = useState(false);
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const [showModalBusiness, setShowModalBusiness] = useState(false);

  // Use custom hook for academic application logic
  const {
    trialType,
    academicStatus,
    loadingStatus,
    submitAcademicApplication,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email);

  // Use tag-based logic from hook
  const isCommercialList = isCommercialFromHook();
  const canSeePricing = canSeePricesFromHook();

  const handleUpgradeClick = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowModalAcademic(false);
    setShowModalBusiness(false);
  };

  const handleApplyToAcademicClick = () => setShowModalAcademic(true);

  const handleSubmit = e => {
    e.preventDefault();
    // No longer need to show logout modal - Stripe handles the upgrade flow
    setShowModal(false);
  };

  const handleBusinessSubmit = async () => {

    if (!user?.email) {
      Swal.fire('User email not found.');
      return;
    }
    
    setShowModalBusiness(false);

    try {
      const response = await RequestPricingService.requestPricingFromProfile(
        user?.email || '',
        user?.['custom:name'] || 'Unknown',
        user?.['custom:company'] || 'Unknown',
        user?.['custom:application_role'] || 'Unknown'
      );

      if (response.success) {
        Swal.fire({
          title: 'Success!',
          text: response.message,
          customClass: {
            confirmButton: 'btn-gradient-pink',
          },
        });
      } else {
        console.error('Error in pricing request:', response.error);
        Swal.fire('Oops! Something went wrong.');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error with your request. Try again later.');
    }
    setShowModalAcademic(false);
  };

  let buttonsToRender = null;

  if (loadingStatus) {
    buttonsToRender = <div>Loading...</div>;
  } else if (isTrial && trialType === 'academic') {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application has been approved</p>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <div className="text-right">
            <SharedButton variant="gradient" onClick={handleApplyToAcademicClick}>
              Apply for Free Academic Plan
            </SharedButton>
          </div>
        </div>
      );
    }
  } else if (isTrial && trialType === 'commercial') {
    buttonsToRender = (
      <div>
        <SharedButton variant="primary"
          onClick={() =>
            window.open('/book-a-demo?utm_source=web', '_blank', 'noopener,noreferrer')
          }
        >
          Schedule a demo
        </SharedButton>
        <SharedButton variant="gradient" onClick={() => setShowModalBusiness(true)}>
          Request Pricing
        </SharedButton>
      </div>
    );
  } else if (isTrial && !isCommercialList) {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
          <SharedButton variant="gradient" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </SharedButton>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <SharedButton variant="secondary" onClick={handleApplyToAcademicClick}>
            Pay for Academic Plan
          </SharedButton>
          <SharedButton variant="gradient" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </SharedButton>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <SharedButton variant="secondary" onClick={handleApplyToAcademicClick}>
            Apply for Free Academic Plan
          </SharedButton>
          <SharedButton variant="gradient" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </SharedButton>
        </div>
      );
    }
  } else if (isTrial && isCommercialList) {
    buttonsToRender = (
      <div>
        <SharedButton variant="secondary"
          onClick={() =>
            window.open('/book-a-demo?utm_source=web', '_blank', 'noopener,noreferrer')
          }
        >
            Schedule a demo
        </SharedButton>
        <SharedButton variant="gradient" onClick={() => setShowModalBusiness(true)}>
          Request Pricing
        </SharedButton>
      </div>
    );
  } else if (!isTrial && (isAcademic || isStandard)) {
    if (canSeePricing) {
      buttonsToRender = (
        <div>
          <SharedButton variant="gradient" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </SharedButton>
        </div>
      );
    } else {
      // User cannot see pricing - different behavior for academic vs commercial
      if (isAcademic) {
        // Academic users get contact button
        buttonsToRender = (
          <div className="grid justify-items-end">
            <SharedButton variant="gradient">
              <a href="/Support" className="text-white">
                Contact Us
              </a>
            </SharedButton>
          </div>
        );
      } else {
        // Commercial users get request pricing
        buttonsToRender = (
          <div className="grid justify-items-end">
            <p className="mb-0">Do you want to upgrade your plan?</p>
            <SharedButton variant="gradient" onClick={() => setShowModalBusiness(true)}>
              Request Pricing
            </SharedButton>
          </div>
        );
      }
    }
  } else {
    // Fallback - no condition matched
    console.warn('No button condition matched. User state:', {
      isTrial,
      isAcademic,
      isStandard,
      trialType,
      academicStatus,
    });
  }

  return (
    <div>
      {buttonsToRender}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="popup-upgrade">
            <button className="modal-close-btn" onClick={handleCloseModal}>
              <XMarkIcon className="h-5 w-5 text-black" />
            </button>

            <h3 className="text-center">Upgrade Your Plan</h3>

            {!canSeePricing ? (
              <div className="text-center p-4">
                <p className="mb-4">Contact us for pricing.</p>
                <button className="btn-gradient-pink">
                  <a href="/Support" className="text-white">
                    Contact us
                  </a>
                </button>
              </div>
            ) : isStandard ? (
              <AdvancedPlanOnly />
            ) : (
              <>
                <PricePlanesAcademic />
                <p className="text-muted text-center mt-3" style={{ fontSize: '0.85em' }}>
                  Not academic?{' '}
                  <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                    Contact us
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <AcademicApplicationModal
        isOpen={showModalAcademic}
        onClose={() => setShowModalAcademic(false)}
        onSubmit={submitAcademicApplication}
        academicStatus={academicStatus}
      />

      {/* Modal Business */}
      {showModalBusiness && (
        <div className="modal-overlay">
          <div className="RegisterPopup">
            <div className="apply-popup-container">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <XMarkIcon className="h-5 w-5 text-black" />
              </button>
              <>
                <h4 className="m-2">Are you sure you want to send a request for pricing?</h4>
                <SharedButton
                  variant="gradient"
                  size="md"
                  onClick={handleBusinessSubmit}
                  aria-label="submit-modal"
                >
                  Send Request
                </SharedButton>
                <SharedButton 
                  variant="secondary" 
                  size="md"
                  onClick={handleCloseModal} 
                  aria-label="cancel-modal">
                  Cancel
                </SharedButton>
              </>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradePlan;
